import { Link } from 'react-router-dom';
import { LegalLayout } from './LegalLayout';
import { COMPANY, LEGAL_ROUTES } from './legal';

export function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      active="terms"
      intro={
        <>
          These Terms govern your access to and use of Revvly, the subscription and
          revenue-management platform operated by {COMPANY.legalName} ({COMPANY.companyNumber}).
        </>
      }
    >
      <p>
        These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of Revvly,
        the subscription and revenue-management platform (the &ldquo;Service&rdquo;) operated by{' '}
        {COMPANY.legalName}, a company incorporated in {COMPANY.jurisdiction} (&ldquo;AcceleratorApp,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account, clicking to
        accept, or otherwise accessing or using the Service, you agree to be bound by these Terms. If
        you are entering into these Terms on behalf of a company or other legal entity, you represent
        that you have authority to bind that entity, and &ldquo;you&rdquo; and &ldquo;Customer&rdquo;
        refer to that entity.
      </p>
      <p>
        If you have a separately signed Master Services Agreement (MSA) or order form with us that
        covers Revvly, that agreement controls to the extent it conflicts with these Terms.
      </p>

      <h2>1. Definitions</h2>
      <ul>
        <li>
          <strong>&ldquo;Authorized User&rdquo;</strong> means an individual you permit to access the
          Service under your account (for example, an employee, contractor, or team member).
        </li>
        <li>
          <strong>&ldquo;Customer Data&rdquo;</strong> means data, content, and information you or
          your Authorized Users submit to or generate within the Service.
        </li>
        <li>
          <strong>&ldquo;End Customer&rdquo;</strong> means a person or entity from whom you collect
          payments or manage subscriptions using the Service.
        </li>
        <li>
          <strong>&ldquo;Payment Processor&rdquo;</strong> means the third-party payment provider
          (currently Stripe, Inc. and its affiliates) that processes payment transactions initiated
          through the Service.
        </li>
        <li>
          <strong>&ldquo;Subscription&rdquo;</strong> means your paid plan for access to the Service,
          as described in your order or in-product checkout.
        </li>
      </ul>

      <h2>2. Accounts and Access</h2>
      <h3>2.1 Registration</h3>
      <p>
        To use the Service you must create an account and provide accurate, current, and complete
        information. You are responsible for keeping your registration details up to date.
      </p>
      <h3>2.2 Account security</h3>
      <p>
        You are responsible for safeguarding your account credentials and for all activity that
        occurs under your account, whether or not authorized by you. Notify us promptly at{' '}
        <a href={`mailto:${COMPANY.securityEmail}`}>{COMPANY.securityEmail}</a> if you suspect any
        unauthorized access or use.
      </p>
      <h3>2.3 Authorized Users</h3>
      <p>
        You may permit Authorized Users to access the Service subject to these Terms. You are
        responsible for your Authorized Users&rsquo; compliance, and any act or omission by an
        Authorized User that would breach these Terms is deemed your breach.
      </p>

      <h2>3. Subscriptions, Fees, and Billing</h2>
      <h3>3.1 Subscription fees</h3>
      <p>
        Access to the Service is provided on a subscription basis. Fees, billing frequency, and
        included usage are set out at the point of purchase or in your order. Unless stated
        otherwise, subscription fees are billed in advance and are non-refundable except as required
        by law or expressly stated in these Terms.
      </p>
      <h3>3.2 Usage-based charges</h3>
      <p>
        Certain features are billed based on usage (for example, transaction volume or
        payment-processing activity). Usage charges accrue during each billing period and are
        invoiced in arrears. We may set, and change, usage rates on notice as described in Section
        3.5.
      </p>
      <h3>3.3 Payment-processing fees</h3>
      <p>
        Payments you collect from End Customers through the Service are processed by the Payment
        Processor. Processing fees charged by the Payment Processor, and any platform fee we charge in
        connection with payment facilitation, will be disclosed to you and are in addition to your
        subscription fees. You authorize us and the Payment Processor to deduct applicable fees from
        processed amounts before remittance.
      </p>
      <h3>3.4 Taxes</h3>
      <p>
        Fees are exclusive of taxes. You are responsible for all sales, use, VAT, GST, withholding,
        and other taxes associated with your purchase, excluding taxes based on our net income.
      </p>
      <h3>3.5 Changes to fees</h3>
      <p>
        We may change subscription fees or usage rates effective at the start of your next renewal
        term, or, for usage-based charges, on at least thirty (30) days&rsquo; notice. Your continued
        use of the Service after a fee change takes effect constitutes acceptance of the new fees.
      </p>
      <h3>3.6 Late payment</h3>
      <p>
        Overdue amounts may accrue interest at 1.5% per month (or the maximum permitted by law, if
        lower). We may suspend the Service for non-payment after reasonable notice.
      </p>

      <h2>4. Payment Processing</h2>
      <h3>4.1 Role of the Service</h3>
      <p>
        Revvly provides technology that enables you to manage subscriptions and collect payments from
        your End Customers. We are a technology provider and payment facilitator, not a bank, money
        transmitter, or the merchant of record for your transactions. You are the merchant of record
        with respect to payments you collect from your End Customers.
      </p>
      <h3>4.2 Payment Processor terms</h3>
      <p>
        Payment processing is provided by the Payment Processor and is subject to the Payment
        Processor&rsquo;s own terms and connected-account agreement (for Stripe, the Stripe Connected
        Account Agreement and Stripe Services Agreement). By using the payment features, you agree to
        those terms, and you authorize us to share transaction and account information with the
        Payment Processor as needed to provide the Service. We are not responsible for the acts,
        errors, or omissions of the Payment Processor.
      </p>
      <h3>4.3 Settlement and payouts</h3>
      <p>
        Payout timing, holds, reserves, and settlement of funds to you are governed by the Payment
        Processor. We do not control, and are not responsible for, the timing or availability of funds
        held or disbursed by the Payment Processor.
      </p>
      <h3>4.4 Refunds, chargebacks, and disputes</h3>
      <p>
        You are responsible for handling refunds, chargebacks, and payment disputes with your End
        Customers in accordance with the Payment Processor&rsquo;s rules and applicable card-network
        rules. You are liable for chargebacks, refunds, and related fees attributable to your
        transactions.
      </p>
      <h3>4.5 Your compliance obligations</h3>
      <p>
        You are responsible for complying with all laws and card-network rules applicable to your
        collection of payments, including consumer-protection, tax, anti-money-laundering, and
        disclosure requirements, and for providing your End Customers with accurate pricing, billing,
        and refund terms.
      </p>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to, and not to permit any Authorized User or third party to:</p>
      <ul>
        <li>use the Service in violation of any applicable law or third-party rights;</li>
        <li>
          use the Service to process payments for goods or services prohibited by the Payment
          Processor or applicable card-network rules;
        </li>
        <li>
          attempt to gain unauthorized access to the Service or its related systems, or probe, scan,
          or test the vulnerability of the Service;
        </li>
        <li>
          reverse-engineer, decompile, or attempt to derive the source code of the Service, except to
          the extent this restriction is prohibited by law;
        </li>
        <li>
          resell, sublicense, or make the Service available to third parties except as expressly
          permitted;
        </li>
        <li>
          interfere with or disrupt the integrity or performance of the Service, or introduce
          malicious code; or
        </li>
        <li>use the Service to transmit unlawful, infringing, deceptive, or harmful content.</li>
      </ul>
      <p>
        We may suspend access without prior notice if we reasonably believe your use poses a security
        risk, may harm our systems or other customers, or violates law or this Section.
      </p>

      <h2>6. Customer Data and Privacy</h2>
      <h3>6.1 Ownership</h3>
      <p>
        As between the parties, you retain all rights in and to Customer Data. You grant us a
        non-exclusive, worldwide license to host, process, and use Customer Data solely to provide,
        maintain, secure, and improve the Service and as otherwise permitted in these Terms.
      </p>
      <h3>6.2 Your responsibilities</h3>
      <p>
        You represent that you have all rights and consents necessary to submit Customer Data to the
        Service and to authorize our processing of it, including any personal data of your Authorized
        Users and End Customers.
      </p>
      <h3>6.3 Data protection</h3>
      <p>
        Where we process personal data on your behalf, we do so as a processor in accordance with our
        Data Processing Agreement (the &ldquo;DPA&rdquo;), which is incorporated into these Terms by
        reference and available on request. To the extent the EU/UK GDPR or comparable data-protection
        laws apply, the DPA governs the parties&rsquo; respective obligations, including any
        international transfers of personal data. Our{' '}
        <Link to={LEGAL_ROUTES.privacy}>Privacy Policy</Link> explains how we handle personal
        information, and our <Link to={LEGAL_ROUTES.cookies}>Cookie Policy</Link> explains our use of
        cookies.
      </p>
      <h3>6.4 Security</h3>
      <p>
        We maintain administrative, technical, and organizational measures designed to protect
        Customer Data appropriate to the nature of the data and the risks involved, consistent with
        our information-security program.
      </p>
      <h3>6.5 Aggregated data</h3>
      <p>
        We may generate and use anonymized and aggregated data derived from use of the Service (which
        does not identify you, any individual, or any End Customer) for analytics, benchmarking, and
        improving our products.
      </p>

      <h2>7. Intellectual Property</h2>
      <p>
        The Service, including all software, interfaces, content, and trademarks, is owned by
        AcceleratorApp and its licensors and is protected by intellectual-property laws. Subject to
        these Terms, we grant you a limited, non-exclusive, non-transferable, revocable right to
        access and use the Service during your subscription term for your internal business purposes.
        We reserve all rights not expressly granted. Any feedback you provide may be used by us
        without restriction or obligation to you.
      </p>

      <h2>8. Third-Party Services</h2>
      <p>
        The Service may integrate with or link to third-party services (including the Payment
        Processor). Your use of third-party services is governed by their terms, and we are not
        responsible for third-party services. We do not warrant and are not liable for the
        availability, accuracy, or performance of any third-party service.
      </p>

      <h2>9. Term and Termination</h2>
      <h3>9.1 Term</h3>
      <p>
        These Terms begin when you first accept them and continue for as long as you have an active
        account or subscription.
      </p>
      <h3>9.2 Termination by you</h3>
      <p>
        You may cancel your Subscription at any time through your account settings or by contacting
        us. Cancellation takes effect at the end of the then-current billing period; fees already paid
        are non-refundable except as required by law.
      </p>
      <h3>9.3 Termination by us</h3>
      <p>
        We may suspend or terminate your access if you materially breach these Terms and, where the
        breach is curable, fail to cure it within fifteen (15) days of notice; if required by law or
        by the Payment Processor; or if you fail to pay amounts due.
      </p>
      <h3>9.4 Effect of termination</h3>
      <p>
        On termination, your right to access the Service ends. We will make Customer Data available
        for export for thirty (30) days after termination, after which we may delete it in the
        ordinary course, subject to the DPA and legal-retention obligations. Termination does not
        relieve you of the obligation to pay amounts accrued before termination.
      </p>
      <h3>9.5 Survival</h3>
      <p>
        Sections that by their nature should survive termination (including Definitions, Fees accrued,
        Intellectual Property, Disclaimers, Limitation of Liability, Indemnification, and Governing
        Law) survive.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES
        OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE
        DISCLAIM ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
        ERROR-FREE, OR SECURE, OR THAT IT WILL MEET YOUR REQUIREMENTS.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, OR
        GOODWILL, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE, EVEN IF ADVISED OF THE
        POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        EXCEPT FOR YOUR PAYMENT OBLIGATIONS AND EITHER PARTY&rsquo;S INDEMNIFICATION OBLIGATIONS, EACH
        PARTY&rsquo;S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT
        EXCEED THE TOTAL SUBSCRIPTION FEES YOU PAID US FOR THE SERVICE IN THE TWELVE (12) MONTHS
        PRECEDING THE EVENT GIVING RISE TO THE CLAIM. FOR THE AVOIDANCE OF DOUBT, AMOUNTS PROCESSED ON
        YOUR BEHALF AND REMITTED TO YOU OR YOUR END CUSTOMERS ARE NOT FEES PAID TO US.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You will defend, indemnify, and hold harmless AcceleratorApp and its officers, directors,
        employees, and agents from and against any claims, damages, liabilities, and costs (including
        reasonable legal fees) arising out of or related to: (a) your use of the Service in violation
        of these Terms or law; (b) Customer Data or content you submit; (c) your relationship with, or
        payments collected from, your End Customers, including refunds, chargebacks, and disputes; or
        (d) your breach of these Terms or of any Payment Processor or card-network rules.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes, we will provide
        reasonable notice (for example, by email or in-product notice) before they take effect. Your
        continued use of the Service after the effective date of the updated Terms constitutes
        acceptance. If you do not agree, you must stop using the Service.
      </p>

      <h2>14. Governing Law and Disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, without regard to its
        conflict-of-laws principles. The parties submit to the exclusive jurisdiction of the state and
        federal courts located in Delaware for any dispute arising out of or relating to these Terms,
        and waive any objection to venue in those courts. The United Nations Convention on Contracts
        for the International Sale of Goods does not apply.
      </p>

      <h2>15. General</h2>
      <h3>15.1 Entire agreement</h3>
      <p>
        These Terms, together with any order, the DPA, and any policies referenced herein, constitute
        the entire agreement between the parties regarding the Service and supersede all prior
        agreements on the subject, except a signed MSA covering the Service, which controls where it
        conflicts.
      </p>
      <h3>15.2 Assignment</h3>
      <p>
        You may not assign these Terms without our prior written consent. We may assign these Terms in
        connection with a merger, acquisition, reorganization, or sale of assets.
      </p>
      <h3>15.3 Force majeure</h3>
      <p>
        Neither party is liable for delay or failure to perform due to causes beyond its reasonable
        control.
      </p>
      <h3>15.4 Severability and waiver</h3>
      <p>
        If any provision is held unenforceable, the remaining provisions remain in effect. A failure
        to enforce a provision is not a waiver of it.
      </p>
      <h3>15.5 Notices</h3>
      <p>
        Legal notices to us should be sent to{' '}
        <a href={`mailto:${COMPANY.legalEmail}`}>{COMPANY.legalEmail}</a>. We may give notice to you
        via the email associated with your account or through the Service.
      </p>
      <h3>15.6 Relationship</h3>
      <p>
        The parties are independent contractors. These Terms do not create a partnership, agency, or
        joint venture.
      </p>

      <h2>Contact</h2>
      <div className="legal__contact">
        <p>
          <strong>{COMPANY.legalName}</strong> — {COMPANY.companyNumber}
        </p>
        <p>
          Legal: <a href={`mailto:${COMPANY.legalEmail}`}>{COMPANY.legalEmail}</a>
        </p>
        <p>
          Security: <a href={`mailto:${COMPANY.securityEmail}`}>{COMPANY.securityEmail}</a>
        </p>
      </div>
    </LegalLayout>
  );
}
